import React, { useEffect, useState, useRef } from "react";
import noAvatar from "../../../../common/img/no-avatar.webp";
import styles from "./user-avatar.module.scss";
import classNames from "classnames/bind";
import { useTranslation } from "react-i18next";
import { useUpdateUserMutation } from "../../../../../api/userApi";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../../../store";
import { setSession } from "../../../../../slices/sessionSlice";
import { User } from "@nicsmith/shared_types";
import { AVATARS, getAvatarUrl } from "../../../../onboarding/constants";

const cx = classNames.bind(styles);

const UserAvatar: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [currentAvatar, setCurrentAvatar] = useState<string>(noAvatar);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const user = useSelector((state: RootState) => state.session.user) as User;
  const idToken = useSelector((state: RootState) => state.session.idToken);
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();

  useEffect(() => {
    if (user?.avatar && user.avatar.length > 0) {
      setCurrentAvatar(user.avatar);
    } else {
      setCurrentAvatar(noAvatar);
    }
  }, [user?.avatar]);

  // Close picker when clicking outside
  useEffect(() => {
    if (!showPicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPicker]);

  const handleSelectPresetAvatar = async (avatarUrl: string) => {
    if (!user?.id || !idToken) return;
    setIsLoading(true);
    setShowPicker(false);

    const previousAvatar = user.avatar;

    // Optimistically update UI immediately
    dispatch(setSession({ user: { ...user, avatar: avatarUrl }, idToken }));
    setCurrentAvatar(avatarUrl);

    try {
      await updateUser({
        id: user.id.toString(),
        data: { ...user, avatar: avatarUrl },
      }).unwrap();
    } catch (error) {
      console.error("Error updating avatar:", error);
      // Revert on failure
      dispatch(setSession({ user: { ...user, avatar: previousAvatar }, idToken }));
      setCurrentAvatar(previousAvatar || noAvatar);
    } finally {
      setIsLoading(false);
    }
  };

  const getAvatarSrc = () => {
    if (!currentAvatar || currentAvatar === noAvatar) return noAvatar;
    if (currentAvatar.includes("googleusercontent.com")) {
      const baseUrl = currentAvatar.split("=")[0];
      return `${baseUrl}=s96-c`;
    }
    return currentAvatar;
  };

  return (
    <div className={cx("userAvatar")} ref={pickerRef}>
      <div className={cx("avatarBlock")}>
        <div className={cx("avatar", { noAvatar: currentAvatar === noAvatar })}>
          {isLoading || isUpdating ? (
            <div className={cx("loadingSpinner")}></div>
          ) : (
            <img
              src={getAvatarSrc()}
              alt={t("userAvatar.altText")}
              loading="lazy"
              referrerPolicy="no-referrer"
              onError={(e) => {
                if (e.currentTarget.src !== noAvatar) {
                  e.currentTarget.src = noAvatar;
                }
              }}
            />
          )}
        </div>

        <div
          className={cx("cameraIcon")}
          onClick={() => setShowPicker((v) => !v)}
        >
          <i className="fas fa-pen" />
        </div>
      </div>

      <div
        className={cx("changeBtn")}
        onClick={() => setShowPicker((v) => !v)}
      >
        {t("userAvatar.changeAvatar", "Change avatar")}
      </div>

      {showPicker && (
        <div className={cx("avatarPicker")}>
          <div className={cx("pickerGrid")}>
            {AVATARS.map((name) => {
              const url = getAvatarUrl(name);
              const isSelected = currentAvatar === url;
              return (
                <button
                  key={name}
                  type="button"
                  className={cx("pickerItem", { pickerItemSelected: isSelected })}
                  onClick={() => handleSelectPresetAvatar(url)}
                >
                  <img src={url} alt={name} className={cx("pickerImage")} />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAvatar;
